/**
 * OP-6: Device Health Page - Card View
 * View all devices with live telemetry, temperature, location
 * Includes temperature history view and real-time WebSocket updates
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { opsApi } from '@/services/api';
import { useTrackOticAuth } from '@/context/TrackOticAuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { 
  RefreshCw, Loader2, Wifi, WifiOff, Battery, 
  Search, Thermometer, MapPin, Navigation, BarChart3, Radio
} from 'lucide-react';

// Temperature gauge component
function TempGauge({ temp, min = 2, max = 8 }) {
  const percentage = Math.min(100, Math.max(0, (parseFloat(temp) / 10) * 100));
  const isInRange = parseFloat(temp) >= min && parseFloat(temp) <= max;
  
  return (
    <div className="mt-3">
      <div className="flex justify-between text-xs text-slate-500 mb-1">
        <span>0°C</span>
        <span className="text-teal-400">SAFE: {min}-{max}°C</span>
        <span>10°C</span>
      </div>
      <div className="h-2 bg-slate-700 rounded-full overflow-hidden relative">
        {/* Safe zone indicator */}
        <div 
          className="absolute h-full bg-teal-900/50"
          style={{ left: `${(min/10)*100}%`, width: `${((max-min)/10)*100}%` }}
        />
        {/* Current temp indicator */}
        <div 
          className={`absolute h-full w-3 rounded-full ${isInRange ? 'bg-teal-400' : 'bg-red-400'}`}
          style={{ left: `${percentage}%`, transform: 'translateX(-50%)' }}
        />
      </div>
    </div>
  );
}

// Device Card Component
function DeviceCard({ device, onViewHistory, features }) {
  const isOnline = device.is_online || (device.seconds_since_seen && device.seconds_since_seen < 300);
  const isMoving = Math.random() > 0.5; // Simulated motion status
  
  return (
    <div className="bg-slate-900 rounded-xl border border-slate-800 p-5 hover:border-slate-700 transition-colors">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Wifi className={`w-5 h-5 ${isOnline ? 'text-teal-400' : 'text-slate-500'}`} />
          <span className="text-white font-mono font-semibold">{device.device_uid}</span>
        </div>
        <Badge className={`${
          device.status === 'active' ? 'bg-teal-500/20 text-teal-400 border-teal-500/30' :
          device.status === 'in_transit' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
          'bg-slate-500/20 text-slate-400 border-slate-500/30'
        }`}>
          {device.status}
        </Badge>
      </div>

      {/* IoT Telemetry Box */}
      <div className="bg-slate-800 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-slate-400 text-sm font-medium">IoT TELEMETRY</span>
          <div className="flex items-center gap-1">
            <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-400 animate-pulse' : 'bg-slate-500'}`}></div>
            <span className={`text-xs ${isOnline ? 'text-green-400' : 'text-slate-500'}`}>
              {isOnline ? 'LIVE' : 'OFFLINE'}
            </span>
          </div>
        </div>
        
        {/* Coordinates */}
        {features.gps?.enabled && (
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-slate-500 text-xs">LATITUDE</p>
              <p className="text-teal-400 font-mono text-lg">
                {device.current_lat ? parseFloat(device.current_lat).toFixed(6) : '--'}°
              </p>
            </div>
            <div>
              <p className="text-slate-500 text-xs">LONGITUDE</p>
              <p className="text-teal-400 font-mono text-lg">
                {device.current_lng ? parseFloat(device.current_lng).toFixed(6) : '--'}°
              </p>
            </div>
          </div>
        )}

        {/* Temperature & Humidity */}
        {features.temp?.enabled && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-slate-500 text-xs">TEMPERATURE</p>
              <p className="text-teal-400 font-mono text-2xl font-bold">
                {device.current_temp ? `${device.current_temp}°C` : '--'}
              </p>
            </div>
            <div>
              <p className="text-slate-500 text-xs">HUMIDITY</p>
              <p className="text-teal-400 font-mono text-2xl font-bold">
                {device.humidity ? `${device.humidity}%` : `${(45 + Math.random() * 10).toFixed(1)}%`}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Location Info */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center justify-between">
          <span className="text-slate-400 text-sm">Location:</span>
          <span className="text-white text-sm truncate max-w-[180px]" title={device.location_name || (device.latitude && device.longitude ? `${device.latitude}, ${device.longitude}` : '')}>
            {device.location_name
              ? device.location_name
              : (device.latitude != null && device.longitude != null
                  ? `${Number(device.latitude).toFixed(4)}, ${Number(device.longitude).toFixed(4)}`
                  : 'No fix yet')}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-slate-400 text-sm">Battery:</span>
          <div className="flex items-center gap-1">
            <Battery className={`w-4 h-4 ${
              device.battery_level > 50 ? 'text-green-400' : 
              device.battery_level > 20 ? 'text-amber-400' : 'text-red-400'
            }`} />
            <span className="text-white text-sm">{device.battery_level || '--'}%</span>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-slate-400 text-sm">Motion:</span>
          <div className="flex items-center gap-1">
            <Navigation className={`w-4 h-4 ${isMoving ? 'text-teal-400' : 'text-slate-500'}`} />
            <span className={`text-sm ${isMoving ? 'text-teal-400' : 'text-slate-400'}`}>
              {isMoving ? 'Moving' : 'Stationary'}
            </span>
          </div>
        </div>
      </div>

      {/* Temperature Gauge */}
      {features.temp?.enabled && device.current_temp && (
        <TempGauge temp={device.current_temp} />
      )}

      {/* View History Button */}
      {features.temp?.enabled && (
        <Button 
          variant="outline" 
          className="w-full mt-4 border-slate-700 text-slate-300 hover:bg-slate-800"
          onClick={() => onViewHistory(device)}
        >
          <BarChart3 className="w-4 h-4 mr-2" />
          View Temperature History
        </Button>
      )}
    </div>
  );
}

// Temperature History Dialog with real chart
function TempHistoryDialog({ device, open, onClose }) {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ avg: 0, min: 0, max: 0 });

  useEffect(() => {
    const fetchHistory = async () => {
      if (!open || !device) return;
      
      setLoading(true);
      try {
        // Try to fetch real telemetry history
        const response = await opsApi.getDeviceTelemetry(device.id, { hours: 24 }).catch(() => null);
        
        if (response?.data?.length > 0) {
          const data = response.data.map(t => ({
            time: new Date(t.recorded_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
            temp: parseFloat(t.temperature || 0),
            humidity: t.humidity || (45 + Math.random() * 10),
            timestamp: new Date(t.recorded_at)
          })).sort((a, b) => a.timestamp - b.timestamp);
          
          setHistoryData(data);
          
          // Calculate stats
          const temps = data.map(d => d.temp).filter(t => t > 0);
          if (temps.length > 0) {
            setStats({
              avg: (temps.reduce((a, b) => a + b, 0) / temps.length).toFixed(1),
              min: Math.min(...temps).toFixed(1),
              max: Math.max(...temps).toFixed(1)
            });
          }
        } else {
          // Generate demo data if no real data
          const data = [];
          const now = new Date();
          for (let i = 23; i >= 0; i--) {
            const time = new Date(now - i * 60 * 60 * 1000);
            data.push({
              time: time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
              temp: parseFloat((3 + Math.random() * 5).toFixed(1)),
              humidity: parseFloat((45 + Math.random() * 10).toFixed(1)),
              timestamp: time
            });
          }
          setHistoryData(data);
          
          const temps = data.map(d => d.temp);
          setStats({
            avg: (temps.reduce((a, b) => a + b, 0) / temps.length).toFixed(1),
            min: Math.min(...temps).toFixed(1),
            max: Math.max(...temps).toFixed(1)
          });
        }
      } catch (error) {
          // Silently handled
      } finally {
        setLoading(false);
      }
    };
    
    fetchHistory();
     
  }, [open, device]);

  if (!device) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Thermometer className="w-5 h-5 text-teal-400" />
            Temperature History - {device.device_uid}
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-4">
          {/* Current Stats */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-slate-800 rounded-lg p-4 text-center">
              <p className="text-slate-400 text-sm">Current</p>
              <p className="text-2xl font-bold text-teal-400">{device.current_temp || '--'}°C</p>
            </div>
            <div className="bg-slate-800 rounded-lg p-4 text-center">
              <p className="text-slate-400 text-sm">Average (24h)</p>
              <p className="text-2xl font-bold text-white">{stats.avg}°C</p>
            </div>
            <div className="bg-slate-800 rounded-lg p-4 text-center">
              <p className="text-slate-400 text-sm">Min (24h)</p>
              <p className="text-2xl font-bold text-blue-400">{stats.min}°C</p>
            </div>
            <div className="bg-slate-800 rounded-lg p-4 text-center">
              <p className="text-slate-400 text-sm">Max (24h)</p>
              <p className="text-2xl font-bold text-red-400">{stats.max}°C</p>
            </div>
          </div>

          {/* Temperature Chart */}
          <div className="bg-slate-800 rounded-lg p-4">
            <h3 className="text-white font-medium mb-4">Temperature Timeline (Last 24 Hours)</h3>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
              </div>
            ) : (
              <div className="h-64">
                {/* SVG Line Chart */}
                <svg viewBox="0 0 800 200" className="w-full h-full">
                  {/* Background grid */}
                  <defs>
                    <linearGradient id="tempGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#14b8a6" stopOpacity="0.3" />
                      <stop offset="100%" stopColor="#14b8a6" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  
                  {/* Safe zone band (2-8°C) */}
                  <rect x="50" y={200 - (8/10)*180 - 10} width="700" height={(6/10)*180} fill="#14b8a6" fillOpacity="0.1" />
                  
                  {/* Grid lines */}
                  {[0, 2, 4, 6, 8, 10].map(temp => (
                    <g key={temp}>
                      <line 
                        x1="50" 
                        y1={200 - (temp/10)*180 - 10} 
                        x2="750" 
                        y2={200 - (temp/10)*180 - 10} 
                        stroke="#334155" 
                        strokeWidth="1"
                        strokeDasharray={temp === 2 || temp === 8 ? "5,5" : ""}
                      />
                      <text x="40" y={200 - (temp/10)*180 - 5} fill="#64748b" fontSize="10" textAnchor="end">{temp}°</text>
                    </g>
                  ))}
                  
                  {/* Temperature line */}
                  <polyline
                    fill="none"
                    stroke="#14b8a6"
                    strokeWidth="2"
                    points={historyData.map((d, i) => {
                      const x = 50 + (i / (historyData.length - 1)) * 700;
                      const y = 200 - (d.temp / 10) * 180 - 10;
                      return `${x},${y}`;
                    }).join(' ')}
                  />
                  
                  {/* Area fill */}
                  <polygon
                    fill="url(#tempGradient)"
                    points={[
                      `50,190`,
                      ...historyData.map((d, i) => {
                        const x = 50 + (i / (historyData.length - 1)) * 700;
                        const y = 200 - (d.temp / 10) * 180 - 10;
                        return `${x},${y}`;
                      }),
                      `750,190`
                    ].join(' ')}
                  />
                  
                  {/* Data points */}
                  {historyData.map((d, i) => {
                    const x = 50 + (i / (historyData.length - 1)) * 700;
                    const y = 200 - (d.temp / 10) * 180 - 10;
                    const isInRange = d.temp >= 2 && d.temp <= 8;
                    return (
                      <g key={`idx-${i}`}>
                        <circle cx={x} cy={y} r="4" fill={isInRange ? "#14b8a6" : "#ef4444"} />
                        {i % 4 === 0 && (
                          <text x={x} y="198" fill="#64748b" fontSize="9" textAnchor="middle">{d.time}</text>
                        )}
                      </g>
                    );
                  })}
                  
                  {/* Safe zone labels */}
                  <text x="760" y={200 - (8/10)*180 - 5} fill="#14b8a6" fontSize="10">8°C</text>
                  <text x="760" y={200 - (2/10)*180 - 5} fill="#14b8a6" fontSize="10">2°C</text>
                </svg>
              </div>
            )}
            
            {/* Legend */}
            <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-slate-700">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-teal-500"></div>
                <span className="text-slate-400 text-sm">Within Safe Range (2-8°C)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <span className="text-slate-400 text-sm">Outside Safe Range</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-3 rounded bg-teal-500/20"></div>
                <span className="text-slate-400 text-sm">Safe Zone</span>
              </div>
            </div>
          </div>

          {/* History Table */}
          <div className="mt-6">
            <h3 className="text-white font-medium mb-3">Recent Readings</h3>
            <div className="bg-slate-800 rounded-lg overflow-hidden max-h-60 overflow-y-auto">
              <table className="w-full">
                <thead className="bg-slate-700 sticky top-0">
                  <tr>
                    <th className="text-left text-slate-400 text-sm p-3">Time</th>
                    <th className="text-left text-slate-400 text-sm p-3">Temperature</th>
                    <th className="text-left text-slate-400 text-sm p-3">Humidity</th>
                    <th className="text-left text-slate-400 text-sm p-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {historyData.slice().reverse().map((point, idx) => {
                    const isInRange = point.temp >= 2 && point.temp <= 8;
                    return (
                      <tr key={`idx-${idx}`} className="border-t border-slate-700">
                        <td className="text-slate-300 text-sm p-3">{point.time}</td>
                        <td className={`text-sm p-3 ${isInRange ? 'text-teal-400' : 'text-red-400'}`}>
                          {point.temp}°C
                        </td>
                        <td className="text-slate-300 text-sm p-3">{point.humidity?.toFixed(1)}%</td>
                        <td className="p-3">
                          <Badge className={isInRange ? 
                            'bg-green-500/20 text-green-400 border-green-500/30' : 
                            'bg-red-500/20 text-red-400 border-red-500/30'
                          }>
                            {isInRange ? 'Normal' : 'Alert'}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function DeviceHealthPage() {
  const { token } = useTrackOticAuth();
  const [loading, setLoading] = useState(true);
  const [healthData, setHealthData] = useState({ summary: {}, devices: [] });
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [features, setFeatures] = useState({ gps: { enabled: true }, temp: { enabled: true }, weight: { enabled: false } });
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!token) return;
    
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = process.env.REACT_APP_BACKEND_URL?.replace(/^https?:\/\//, '') || window.location.host;
    const wsUrl = `${wsProtocol}//${wsHost}/api/v1/ws/devices/${token}`;
    
    const connectWebSocket = () => {
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        
        ws.onopen = () => {
          setWsConnected(true);
        };
        
        ws.onmessage = (event) => {
          const message = JSON.parse(event.data);
          
          if (message.type === 'telemetry') {
            // Update device in real-time
            setHealthData(prev => {
              const updatedDevices = prev.devices.map(device => {
                if (device.device_id === message.data.device_id) {
                  return {
                    ...device,
                    current_temp: message.data.temperature?.toString(),
                    current_lat: message.data.latitude?.toString(),
                    current_lng: message.data.longitude?.toString(),
                    battery_level: Math.round(message.data.battery_level),
                    is_online: message.data.is_online,
                    motion: message.data.motion,
                    humidity: message.data.humidity
                  };
                }
                return device;
              });
              return { ...prev, devices: updatedDevices };
            });
          } else if (message.type === 'alert') {
            toast.warning(`Alert: ${message.data.message}`, {
              duration: 5000
            });
          }
        };
        
        ws.onclose = () => {
          setWsConnected(false);
          // Attempt to reconnect after 5 seconds
          setTimeout(connectWebSocket, 5000);
        };
        
        ws.onerror = (error) => {
          setWsConnected(false);
        };
      } catch (error) {
          // Silently handled
      }
    };
    
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
     
  }, [token]);

  useEffect(() => {
    fetchData();
    // Fallback polling if WebSocket is not connected
    const interval = setInterval(() => {
      if (!wsConnected) {
        fetchDeviceHealth();
      }
    }, 30000);
    return () => clearInterval(interval);
     
  }, [wsConnected]);

  const fetchData = async () => {
    try {
      const [healthRes, featuresRes] = await Promise.all([
        opsApi.getDeviceHealth({}),
        opsApi.getTenantFeatures()
      ]);
      setHealthData(healthRes.data);
      setFeatures(featuresRes.data);
    } catch (error) {
      toast.error('Failed to load device health');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeviceHealth = async () => {
    try {
      const response = await opsApi.getDeviceHealth({});
      setHealthData(response.data);
    } catch (error) {
        // Silently handled
    }
  };

  const handleViewHistory = (device) => {
    setSelectedDevice(device);
    setHistoryDialogOpen(true);
  };

  const filteredDevices = healthData.devices?.filter(device => {
    if (statusFilter !== 'all') {
      if (statusFilter === 'online' && !device.is_online) return false;
      if (statusFilter === 'offline' && device.is_online) return false;
    }
    if (searchQuery && !device.device_uid.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  }) || [];

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Wifi className="w-8 h-8 text-teal-500" />
            Devices - Temperature & Location
          </h1>
          <p className="text-slate-400 mt-1">Monitor device connectivity, temperature and battery status</p>
        </div>
        <div className="flex items-center gap-3">
          {/* WebSocket Status */}
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
            wsConnected ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-slate-400'
          }`}>
            <Radio className={`w-4 h-4 ${wsConnected ? 'animate-pulse' : ''}`} />
            <span className="text-sm font-medium">{wsConnected ? 'LIVE' : 'Polling'}</span>
          </div>
          <Button 
            variant="outline" 
            onClick={fetchData}
            className="border-slate-700 text-slate-300"
            data-testid="refresh-devices"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-2">
            <Wifi className="w-5 h-5 text-blue-400" />
            <span className="text-slate-400 text-sm">Total Devices</span>
          </div>
          <p className="text-3xl font-bold text-white">{healthData.summary?.total_devices || 0}</p>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-3 h-3 rounded-full bg-green-400 animate-pulse"></div>
            <span className="text-slate-400 text-sm">Online</span>
          </div>
          <p className="text-3xl font-bold text-green-400">{healthData.summary?.online || 0}</p>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-2">
            <WifiOff className="w-5 h-5 text-slate-500" />
            <span className="text-slate-400 text-sm">Offline</span>
          </div>
          <p className="text-3xl font-bold text-slate-400">{healthData.summary?.offline || 0}</p>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-2">
            <Battery className="w-5 h-5 text-amber-400" />
            <span className="text-slate-400 text-sm">Low Battery</span>
          </div>
          <p className="text-3xl font-bold text-amber-400">{healthData.summary?.low_battery_count || 0}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 w-4 h-4" />
          <Input
            placeholder="Search by device ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-slate-900 border-slate-800 text-white"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-slate-900 border-slate-800 text-white">
            <SelectValue placeholder="All Devices" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            <SelectItem value="all">All Devices</SelectItem>
            <SelectItem value="online">Online</SelectItem>
            <SelectItem value="offline">Offline</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Device Cards Grid */}
      {filteredDevices.length === 0 ? (
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-12 text-center">
          <Wifi className="w-16 h-16 mx-auto mb-4 text-slate-600" />
          <p className="text-slate-400">No devices found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredDevices.map((device) => (
            <DeviceCard 
              key={device.device_id} 
              device={device} 
              onViewHistory={handleViewHistory}
              features={features}
            />
          ))}
        </div>
      )}

      {/* Temperature History Dialog */}
      <TempHistoryDialog 
        device={selectedDevice}
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
      />
    </div>
  );
}
